import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, X, FileSpreadsheet, FileText, RefreshCw, CalendarDays } from "lucide-react";
import ImportButton from "@/components/ImportButton";

const todayISO = () => {
  const t = new Date();
  const off = t.getTimezoneOffset();
  return new Date(t.getTime() - off * 60 * 1000).toISOString().slice(0, 10);
};

const monthStartISO = () => {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10);
};

const quarterStartISO = () => {
  const t = new Date();
  const q = Math.floor(t.getMonth() / 3);
  return new Date(t.getFullYear(), q * 3, 1).toISOString().slice(0, 10);
};

const RANGES = ["This Month", "This Quarter", "Last 30 Days", "Custom"];

function eachDay(start, end) {
  const out = [];
  const s = new Date(start);
  const e = new Date(end);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(new Date(d).toISOString().slice(0, 10));
  }
  return out;
}

export default function Attendance() {
  const { user: authUser } = useAuth();
  const isAdmin = ["admin", "branch"].includes(authUser?.role);
  const canEdit = isAdmin || !!authUser?.permissions?.["Attendance"]?.edit;

  const [rng, setRng] = useState("This Month");
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [absentDlg, setAbsentDlg] = useState({ open: false, user: null, date: null, reason: "" });
  const [staffFilter, setStaffFilter] = useState("all"); // "all" | user.id

  const applyRange = (r) => {
    setRng(r);
    if (r === "This Month") { setDateFrom(monthStartISO()); setDateTo(todayISO()); }
    else if (r === "This Quarter") { setDateFrom(quarterStartISO()); setDateTo(todayISO()); }
    else if (r === "Last 30 Days") {
      const t = new Date(); t.setDate(t.getDate() - 29);
      setDateFrom(t.toISOString().slice(0, 10));
      setDateTo(todayISO());
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance", { params: { date_from: dateFrom, date_to: dateTo } });
      setRecords(data?.records || []);
      setUsers(data?.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load attendance");
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => eachDay(dateFrom, dateTo), [dateFrom, dateTo]);
  const today = todayISO();

  // Build matrix lookup: {userId: {date: {status, reason, source, id}}}
  const matrix = useMemo(() => {
    const m = {};
    for (const r of records) {
      const uid = r.user_id;
      if (!m[uid]) m[uid] = {};
      m[uid][r.date] = r;
    }
    return m;
  }, [records]);

  // Effective status for a cell:
  //   - explicit "absent"  -> absent
  //   - explicit "present" -> present
  //   - blank AND day <= today -> implicit present (default)
  //   - blank AND day > today  -> unknown (future)
  const effectiveStatus = (uid, d) => {
    const rec = matrix[uid]?.[d];
    if (rec?.status === "absent") return "absent";
    if (rec?.status === "present") return "present";
    if (d <= today) return "present-default";
    return "future";
  };

  // Summary: default-present days count as P (up to today).
  const summary = useMemo(() => {
    const s = {};
    const daysUpToToday = days.filter((d) => d <= today);
    for (const u of users) {
      let p = 0, a = 0;
      for (const d of daysUpToToday) {
        const rec = matrix[u.id]?.[d];
        if (rec?.status === "absent") a += 1;
        else p += 1; // present OR implicit present
      }
      s[u.id] = { p, a };
    }
    return s;
  }, [users, days, matrix, today]);

  const markPresent = async (u, date) => {
    try {
      await api.post("/attendance/mark", { user_id: u.id, date, status: "present" });
      toast.success(`Marked ${u.name} present on ${date}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openAbsent = (u, date) => setAbsentDlg({ open: true, user: u, date, reason: matrix[u.id]?.[date]?.reason || "" });

  const submitAbsent = async () => {
    const { user, date, reason } = absentDlg;
    if (!reason.trim()) return toast.error("Reason required");
    try {
      await api.post("/attendance/mark", { user_id: user.id, date, status: "absent", reason: reason.trim() });
      toast.success(`Marked ${user.name} absent on ${date}`);
      setAbsentDlg({ open: false, user: null, date: null, reason: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const download = async (path, fname) => {
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (staffFilter && staffFilter !== "all") params.user_id = staffFilter;
      const res = await api.get(path, { responseType: "blob", params });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      // Encode staff name into filename when a specific staff is picked
      let finalName = fname;
      if (staffFilter && staffFilter !== "all") {
        const s = users.find((u) => u.id === staffFilter);
        if (s) finalName = fname.replace(/(attendance)/i, `$1-${s.name.replace(/\s+/g, "_")}`);
      }
      a.href = url; a.download = finalName; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
  };

  // Filtered view for the matrix — respects the staff selector
  const visibleUsers = useMemo(
    () => (staffFilter === "all" ? users : users.filter((u) => u.id === staffFilter)),
    [users, staffFilter]
  );

  return (
    <div className="space-y-4" data-testid="attendance-page">
      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">Range</Label>
          <Select value={rng} onValueChange={applyRange}>
            <SelectTrigger className="h-9 w-40" data-testid="att-range"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setRng("Custom"); }} className="h-9 w-40" data-testid="att-from" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setRng("Custom"); }} className="h-9 w-40" data-testid="att-to" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">Staff</Label>
          <Select value={staffFilter} onValueChange={setStaffFilter}>
            <SelectTrigger className="h-9 w-56" data-testid="att-staff-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              <SelectItem value="all">— All Staff —</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} — {u.role}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} disabled={loading} className="h-9 bg-amber-500 hover:bg-amber-600 text-white font-semibold">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <div className="flex-1" />
        <Button onClick={() => download("/export/attendance", `attendance-${dateFrom}-to-${dateTo}.xlsx`)} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold" data-testid="att-export-xlsx">
          <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
        </Button>
        <Button onClick={() => download("/export-pdf/attendance", `attendance-${dateFrom}-to-${dateTo}.pdf`)} className="h-9 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold" data-testid="att-export-pdf">
          <FileText className="h-4 w-4 mr-1.5" /> PDF
        </Button>
        {authUser?.role === "admin" && (
          <Button
            onClick={async () => {
              try {
                const res = await api.get(`/export/attendance?all=true`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url; a.download = `attendance-ALL.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Downloaded ALL attendance");
              } catch { toast.error("Failed to download ALL"); }
            }}
            className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold"
            data-testid="att-export-all"
            title="Admin: download every historical attendance record"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export ALL
          </Button>
        )}
        {isAdmin && <ImportButton resource="attendance" onDone={load} label="Import Excel" />}
      </div>

      {/* Matrix */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-2.5 border-b-2 border-[color:var(--brand-teal)] bg-slate-50 flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-[color:var(--brand-teal)]" />
          <div>
            <div className="font-display font-bold text-slate-900 tracking-tight">Staff Attendance</div>
            <div className="text-xs text-slate-500 mt-0.5">
              All staff are marked <span className="font-bold text-emerald-700">Present by default</span> for past &amp; today's dates. Admin/branch — click the ✕ button on a date to mark that staff <span className="font-bold text-red-700">Absent with reason</span>.
            </div>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="text-xs border-collapse">
            <thead className="bg-slate-100 text-slate-700 sticky top-0 z-10">
              <tr>
                <th className="sticky left-0 z-20 bg-slate-100 px-3 py-2 text-left font-semibold border-r border-slate-200 min-w-[200px]">Staff</th>
                {days.map((d) => (
                  <th key={d} className="px-2 py-1.5 text-center font-mono-num font-semibold border-r border-slate-100 min-w-[42px]" title={d}>
                    <div>{d.slice(8)}</div>
                    <div className="text-[9px] text-slate-400 font-normal">{d.slice(5, 7)}</div>
                  </th>
                ))}
                <th className="px-2 py-1.5 text-center font-semibold bg-emerald-50 min-w-[50px]">P</th>
                <th className="px-2 py-1.5 text-center font-semibold bg-red-50 min-w-[50px]">A</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleUsers.length === 0 && (
                <tr><td colSpan={days.length + 3} className="text-center py-10 text-slate-400">No staff to show.</td></tr>
              )}
              {visibleUsers.map((u) => (
                <tr key={u.id}>
                  <td className="sticky left-0 z-10 bg-white px-3 py-2 border-r border-slate-200 font-semibold text-slate-800 min-w-[200px]" data-testid={`att-row-${u.id}`}>
                    <div>{u.name}</div>
                    <div className="text-[10px] text-slate-500 font-normal">{u.role} · {u.email}</div>
                  </td>
                  {days.map((d) => {
                    const rec = matrix[u.id]?.[d];
                    const eff = effectiveStatus(u.id, d);
                    const isPresent = eff === "present" || eff === "present-default";
                    const isAbsent = eff === "absent";
                    const isImplicit = eff === "present-default";
                    const isFuture = eff === "future";
                    return (
                      <td
                        key={d}
                        className={`px-1 py-1 text-center border-r border-slate-100 ${isAbsent ? "bg-red-50" : isPresent ? (isImplicit ? "" : "bg-emerald-50") : ""}`}
                        title={
                          rec
                            ? `${rec.status.toUpperCase()} · ${rec.source}${rec.reason ? " · " + rec.reason : ""} · by ${rec.marked_by}`
                            : isImplicit ? "PRESENT (default) — click ✕ to mark absent" : "Future date"
                        }
                        data-testid={`att-cell-${u.id}-${d}`}
                      >
                        {canEdit ? (
                          <div className="flex items-center justify-center gap-0.5">
                            <button
                              onClick={() => markPresent(u, d)}
                              disabled={isFuture}
                              className={`h-5 w-5 rounded flex items-center justify-center transition-colors ${
                                isPresent
                                  ? (isImplicit ? "bg-emerald-100 text-emerald-700" : "bg-emerald-600 text-white")
                                  : isFuture ? "bg-slate-50 text-slate-300 cursor-not-allowed" : "bg-slate-100 text-slate-400 hover:bg-emerald-100 hover:text-emerald-700"
                              }`}
                              title={isImplicit ? "Present (default)" : "Present"}
                              data-testid={`att-btn-present-${u.id}-${d}`}
                            ><Check className="h-3 w-3" /></button>
                            <button
                              onClick={() => openAbsent(u, d)}
                              disabled={isFuture}
                              className={`h-5 w-5 rounded flex items-center justify-center transition-colors ${isAbsent ? "bg-red-600 text-white" : isFuture ? "bg-slate-50 text-slate-300 cursor-not-allowed" : "bg-slate-100 text-slate-400 hover:bg-red-100 hover:text-red-700"}`}
                              title="Absent (reason required)"
                              data-testid={`att-btn-absent-${u.id}-${d}`}
                            ><X className="h-3 w-3" /></button>
                          </div>
                        ) : (
                          <span className={`inline-flex h-5 w-5 rounded items-center justify-center ${isAbsent ? "bg-red-600 text-white" : isPresent ? (isImplicit ? "text-emerald-500" : "bg-emerald-600 text-white") : "text-slate-300"}`}>
                            {isAbsent ? <X className="h-3 w-3" /> : isPresent ? <Check className="h-3 w-3" /> : "—"}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-center font-mono-num font-bold text-emerald-700 bg-emerald-50/50">{summary[u.id]?.p || 0}</td>
                  <td className="px-2 py-2 text-center font-mono-num font-bold text-red-700 bg-red-50/50">{summary[u.id]?.a || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Absent reason dialog */}
      <Dialog open={absentDlg.open} onOpenChange={(v) => setAbsentDlg((s) => ({ ...s, open: v }))}>
        <DialogContent className="bg-white max-w-md w-[calc(100vw-2rem)] sm:w-full" data-testid="absent-reason-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <X className="h-5 w-5" /> Mark Absent
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm bg-red-50 border border-red-100 rounded px-3 py-2">
              <span className="font-semibold">{absentDlg.user?.name}</span>
              <span className="text-slate-500"> · {absentDlg.date}</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Reason (as informed by staff)</Label>
              <Textarea
                autoFocus rows={3}
                value={absentDlg.reason}
                onChange={(e) => setAbsentDlg((s) => ({ ...s, reason: e.target.value }))}
                placeholder="e.g. Sick leave / Personal work / Family emergency…"
                data-testid="absent-reason-input"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {["Sick leave", "Personal work", "Family function", "Casual leave", "Half-day", "Not informed"].map((p) => (
                <button key={p} type="button" onClick={() => setAbsentDlg((s) => ({ ...s, reason: p }))} className="text-[11px] px-2 py-1 rounded-full bg-slate-100 hover:bg-red-100 hover:text-red-700 text-slate-600">
                  {p}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setAbsentDlg({ open: false, user: null, date: null, reason: "" })}>Cancel</Button>
            <Button onClick={submitAbsent} disabled={!absentDlg.reason.trim()} className="bg-red-600 hover:bg-red-700 text-white" data-testid="absent-save-btn">Mark Absent</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
